// =============================================================================
// features/sales/campaigns/operate/footerMessage.ts — Texto do rodapé quando
// o item reservado já foi encerrado (F35-S18, doc 26 §12).
//
// Três desfechos distintos, três mensagens distintas — nunca uma frase
// genérica de "resultado registrado":
//   - `resolvedMember.status === 'completed'`         → encerramento real,
//     não volta para a fila (outcome terminal — converted/not_interested/
//     do_not_contact).
//   - `resolvedMember.status === 'waiting_callback'`   → retorno agendado
//     para `nextActionAt`, some da fila até essa hora (callback_requested).
//   - qualquer outro valor (sempre `available` no caminho "devolver sem
//     registrar", que usa `release` incondicionalmente) → volta para a fila.
//
// A mensagem SEMPRE lê `resolvedMember.status`, nunca um `outcomeCode` local
// — só o servidor confirma o que realmente aconteceu com o membro.
// =============================================================================

import type { CampaignQueueMember } from '@elemento/shared-schemas';

import { utcIsoToOrgLocalParts } from '../../components/dateTimeOrgTz';

export interface AdvanceMessageInput {
  /** `true` quando o servidor confirmou (409) que o item não é mais nosso. */
  leaseLost: boolean;
  /** `true` quando o item atual já foi liberado (resultado registrado ou devolvido). */
  resolved: boolean;
  /** O `member` devolvido pela chamada (resolve OU release) que produziu `resolved`. */
  resolvedMember: CampaignQueueMember | null;
}

/** "23/08/2026 às 14:00", no fuso da organização — só para leitura no rodapé. */
export function formatOrgDateTime(iso: string): string {
  const { date, time } = utcIsoToOrgLocalParts(iso);
  const [year, month, day] = date.split('-');
  return `${day}/${month}/${year} às ${time}`;
}

/** Mensagem de sucesso exibida no rodapé depois que "Próximo cliente" habilita. */
export function resolveAdvanceMessage({
  leaseLost,
  resolved,
  resolvedMember,
}: AdvanceMessageInput): string {
  if (leaseLost && !resolved) return 'Este item não está mais reservado para você.';

  if (resolvedMember?.status === 'completed') {
    return 'Encerrado. Este cliente não volta para a fila desta campanha.';
  }

  if (resolvedMember?.status === 'waiting_callback' && resolvedMember.nextActionAt) {
    return `Retorno agendado para ${formatOrgDateTime(resolvedMember.nextActionAt)}. O cliente sai da fila até essa hora.`;
  }

  return 'O cliente volta para a fila da campanha e pode ser reservado de novo.';
}
